import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PromotionSettings from './PromotionSettings';
import PromotionTargets from './PromotionTargets';
import PromotionStrategies from './PromotionStrategies';
import PromotionConditions from './PromotionConditions';
import PromotionIncentives from './PromotionIncentives';
import MainButton from '../../../shared/layout/buttons/MainButton';

const PromotionWizardContainer = styled.div`
    padding: 1.875rem;
    background-color: #ffffff;
    border-radius: 12px;
    width: 100%;
    margin: 1.25rem 0;
    border: 1px solid #ddd;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    h3 {
        color: #822292;
        text-align: left; /* Align to the left for consistency */
        margin-bottom: 1.5625rem;
        font-weight: bold;
    }

    .wizard-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 1.875rem;
    }

    @media (max-width: 768px) {
        padding: 1.25rem;

        .wizard-footer {
            flex-direction: column;
            gap: 0.625rem;
            button {
                width: 100%;
            }
        }
    }
`;

const StepIndicator = styled.div`
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 1rem;
    font-weight: 600;
`;

const PromotionWizard = ({ promotion, setPromotion, onBack, onFinish }) => {
    const { t } = useTranslation();
    const steps = [
        { key: 'PromotionSettings', label: t('Campaigns.PromotionWizard.settings', { defaultValue: 'Settings' }) },
        { key: 'PromotionTargets', label: t('Campaigns.PromotionWizard.targets', { defaultValue: 'Targets' }) },
        { key: 'PromotionStrategies', label: t('Campaigns.PromotionWizard.strategies', { defaultValue: 'Strategies' }) },
        { key: 'PromotionConditions', label: t('Campaigns.PromotionWizard.conditions', { defaultValue: 'Conditions' }) },
        { key: 'PromotionIncentives', label: t('Campaigns.PromotionWizard.incentives', { defaultValue: 'Incentives' }) },
    ];

    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    const currentStep = steps[currentStepIndex].key;

    const handleNext = () => {
        if (currentStepIndex < steps.length - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(currentStepIndex - 1);
        }
    };

    return (
        <PromotionWizardContainer>
            <h3>{t('Campaigns.PromotionWizard.title', { defaultValue: 'Promotion Wizard' })}</h3>
            <StepIndicator>
                {t('Campaigns.PromotionWizard.stepIndicator', {
                    defaultValue: `Step ${currentStepIndex + 1} of ${steps.length}`
                })} - {steps[currentStepIndex].label}
            </StepIndicator>
            {currentStep === 'PromotionSettings' && (
                <PromotionSettings
                    promotion={promotion}
                    setPromotion={setPromotion}
                />
            )}
            {currentStep === 'PromotionTargets' && (
                <PromotionTargets
                    promotion={promotion}
                    setPromotion={setPromotion}
                />
            )}
            {currentStep === 'PromotionStrategies' && (
                <PromotionStrategies
                    promotion={promotion}
                    setPromotion={setPromotion}
                />
            )}
            {currentStep === 'PromotionConditions' && (
                <PromotionConditions
                    promotion={promotion}
                    setPromotion={setPromotion}
                />
            )}
            {currentStep === 'PromotionIncentives' && (
                <PromotionIncentives
                    promotion={promotion}
                    setPromotion={setPromotion}
                />
            )}
            <div className="wizard-footer">
                {currentStepIndex > 0 && (
                    <MainButton variant="secondary" onClick={handlePrevious}>
                        {t('Campaigns.PromotionWizard.previous', { defaultValue: 'Previous' })}
                    </MainButton>
                )}
                {currentStepIndex < steps.length - 1 ? (
                    <MainButton onClick={handleNext}>
                        {t('Campaigns.PromotionWizard.next', { defaultValue: 'Next' })}
                    </MainButton>
                ) : (
                    <MainButton onClick={onFinish || onBack}>
                        {t('Campaigns.PromotionWizard.finish', { defaultValue: 'Finish' })}
                    </MainButton>
                )}
            </div>
        </PromotionWizardContainer>
    );
};


export default PromotionWizard;
